import { useEffect, useMemo, useState } from 'react'
import {
  advanceAfterRound,
  commitAndLockForMatch,
  continueToNextRound,
  createInitialState,
  resolveCurrentRound,
  selectAriPlan,
  setPhase,
} from './game/engine'
import type { ChoiceLetter, GamePhase, GameState, Resources, TeamPlayer, TeamSelection, TradeStatus } from './game/types'
import { restoreStaffResearch, toggleResearchTopic } from './game/research'
import { hydratePlayerDevelopment } from './game/progression'
import { isReadyForMatch, getWeeklyActions } from './game/validation'
import { SquadScreen } from './components/SquadScreen'
import { DevelopmentPlan } from './components/DevelopmentPlan'
import { Dashboard } from './components/Dashboard'
import { TeamSelectionOval } from './components/TeamSelectionOval'
import { StaffDevelopment } from './components/StaffDevelopment'
import { TacticalOverrides } from './components/TacticalOverrides'
import { ResearchScreen } from './components/ResearchScreen'
import { LeagueScreen } from './components/LeagueScreen'
import { RecruitmentScreen } from './components/RecruitmentScreen'
import { HistoryScreen } from './components/HistoryScreen'
import { ClubScreen } from './components/ClubScreen'
import { LeftFieldScreen } from './components/LeftFieldScreen'
import { PlayerView } from './components/PlayerView'
import { PLAYER_AGES } from './game/engine'
import { displayedClubRecord, initialiseSeason } from './game/season'
import { reconcileStoredMatchSelection } from './game/selection'
import { beginDraft, createRecruitmentState, hydrateRecruitmentState, openTradePeriod, respondToTradeOffer, scoutProspect, selectDraftProspect, setPlayerTradeStatus, startNextSeasonPreseason, togglePlayerDelisting, toggleProspectWatchlist } from './game/recruitment'
import { createCareerSeed } from './game/matchContext'
import { hydrateCareerHistory } from './game/history'
import { hydrateClubStrategy } from './game/clubStrategy'
import { hydrateContractState } from './game/contracts'
import { hydrateFacilities } from './game/facilities'
import { hydrateStaffMarket } from './game/staffMarket'
import { hydrateSponsorship } from './game/sponsorship'
import { delegateLeftFieldEvent, hydrateLeftFieldState, prepareLeftFieldEvent, resolveLeftFieldChoice } from './game/leftField'
import { hydratePlayerInjuries, repairUnavailableSelections } from './game/injuries'
import { decodeWorkingState, decodeMatchdayState, saveWorkingState, saveMatchdayState, clearWorkingState, clearMatchdayState } from './game/persistence'
import type { SaveState } from './game/types'
import { isTeamAliveInFinals } from './game/competition'

const STAFF_DEVELOPMENT_BASELINE = createInitialState().staffPlan.development.map(allocation => ({ ...allocation }))

const phaseLabels: Partial<Record<GamePhase, string>> = {
  preseason: 'Week',
  headquarters: 'Week',
  'left-field': 'Week',
  'player-detail': 'Player',
  'team-review': 'Team',
  'tactics-review': 'Tactics',
  confirmation: 'Confirm',
  consequences: 'Review',
  'next-round': 'Week',
  squad: 'Squad',
  development: 'Development',
  team: 'Team',
  tactics: 'Tactics',
  research: 'Research',
  board: 'Board',
  staff: 'Staff',
  scouting: 'Scouting',
  recruitment: 'Recruitment',
  'match-day': 'Match Day',
  league: 'League',
  history: 'History',
  club: 'Club',
}

const navigation = ['Week', 'Squad', 'Team', 'Development', 'Tactics', 'Research', 'Recruitment', 'Staff', 'Club', 'League', 'History']

const weekPhaseFor = (state: GameState): GamePhase => {
  if (state.preseasonComplete === false) return 'preseason'
  if (!state.season || state.season.fixtures.length === 0) return 'headquarters'
  const finalRound = Math.max(...state.season.fixtures.map(fixture => fixture.round))
  if (state.season.finals?.status === 'complete') return 'next-round'
  if (state.season.completedThroughRound >= finalRound) return state.phase === 'next-round' ? 'next-round' : 'headquarters'
  return 'headquarters'
}

const loadState = (): GameState => {
  const tryLoad = (decoder: (raw: string | null) => SaveState | null, raw: string | null): SaveState | null => {
    try {
      return decoder(raw)
    } catch {
      return null
    }
  }

  const working = tryLoad(decodeWorkingState, window.localStorage.getItem('bfs-working-state.v56'))
  const matchday = tryLoad(decodeMatchdayState, window.localStorage.getItem('bfs-matchday-autosave.v56'))

  const parsed = working ?? matchday
  if (!parsed?.gameState?.round) return createInitialState(createCareerSeed())

  const gameState = parsed.gameState
  const legacyPreseason = gameState.preseasonComplete === undefined && (gameState.season?.season ?? 1) > 1 && gameState.season?.completedThroughRound === 0 && gameState.round === 1
  const needsPreseasonPlanReset = (legacyPreseason || gameState.preseasonComplete === false) && gameState.preseasonCoachPlanConfirmed === undefined
  const hasResolvedMatch = Boolean(gameState.matchResult && gameState.consequenceReport)
  const savedSeasonComplete = gameState.season?.finals?.status === 'complete'
  const migratedPhase = legacyPreseason
      ? 'preseason'
      : savedSeasonComplete && ['headquarters', 'confirmation', 'match-day', 'consequences'].includes(gameState.phase)
        ? 'next-round'
        : hasResolvedMatch && ['headquarters', 'confirmation', 'match-day'].includes(gameState.phase)
          ? 'consequences'
          : gameState.phase === 'match-day'
            ? 'confirmation'
            : gameState.phase === 'scouting'
              ? 'recruitment'
              : gameState.phase
  const hasLegacyUniformAges = gameState.club.players
      .filter(player => player.name !== 'Ari Dane')
      .every(player => player.age === 25)
  const players = gameState.club.players.map((player: import('./game/types').Player) => {
      const draftedSeason = player.draftedSeason ?? (Number(player.id.match(/^draftee-s(\\d+)-/)?.[1] ?? 0) || undefined)
      return hydratePlayerInjuries(hydratePlayerDevelopment({
        ...player,
        age: hasLegacyUniformAges ? (PLAYER_AGES[player.name] ?? player.age) : player.age,
        draftedSeason,
        draftedByClubId: player.draftedByClubId ?? (draftedSeason ? gameState.season?.userTeamId : undefined),
        clubHistory: player.clubHistory ?? [{
          clubId: gameState.season?.userTeamId ?? 'kingsport',
          fromSeason: draftedSeason ? draftedSeason + 1 : 1,
          arrival: draftedSeason ? 'draft' : 'foundation',
        }],
      }))
  })

  const development = gameState.staffPlan.development.map((allocation: import('./game/types').DevelopmentAllocation) => ({
      ...allocation,
      assistant: allocation.assistant ?? STAFF_DEVELOPMENT_BASELINE.find(baseline => baseline.playerId === allocation.playerId)?.assistant,
  }))

  const initialised = initialiseSeason({
      ...gameState,
      season: gameState.season
        ? { ...gameState.season, careerSeed: gameState.season.careerSeed ?? `legacy-${gameState.season.seed}-${gameState.round}-${Object.keys(gameState.season.results).length}`, matchContexts: gameState.season.matchContexts ?? {} }
        : gameState.season,
      round: gameState.round,
      phase: migratedPhase,
      preseasonComplete: legacyPreseason ? false : gameState.preseasonComplete,
      preseasonCoachPlanConfirmed: needsPreseasonPlanReset ? false : gameState.preseasonCoachPlanConfirmed,
      coachDevelopment: needsPreseasonPlanReset ? { ...gameState.coachDevelopment, isLocked: false } : gameState.coachDevelopment,
      club: { ...gameState.club, players },
      staffPlan: { ...gameState.staffPlan, development },
      researchPlan: gameState.researchPlan,
      researchArchive: gameState.researchArchive ?? [],
  })

  const recruited = {
      ...initialised,
      recruitment: hydrateRecruitmentState(gameState.recruitment, initialised.season?.seed ?? initialised.seed, initialised.season?.season ?? 1, players),
  }

  const withHistory = { ...recruited, history: hydrateCareerHistory(recruited, gameState.history) }
  const withContracts = hydrateContractState(withHistory, gameState)
  const withFacilities = hydrateFacilities({
      ...withContracts,
      clubStrategy: hydrateClubStrategy(withContracts, gameState.clubStrategy),
  }, gameState.facilities)
  const withStaffMarket = hydrateStaffMarket(withFacilities, gameState.staffMarket)
  const hydrated = reconcileStoredMatchSelection(hydrateSponsorship(withStaffMarket, gameState.sponsorship))
  const withRepairedTeam = hydrated.matchResult && hydrated.consequenceReport ? hydrated : repairUnavailableSelections(hydrated)
  return prepareLeftFieldEvent({ ...withRepairedTeam, leftField: hydrateLeftFieldState(gameState.leftField) })
}


const clean = (value: string) => value.replaceAll('\u2014', '-').replaceAll('\u2019', "'")
const signed = (value: number) => `${value > 0 ? '+' : ''}${value}`
const record = (state: GameState) => {
  const current = displayedClubRecord(state)
  return `${current.wins}-${current.losses}-${current.draws}`
}
const scoreText = (score: { goals: number; behinds: number; total?: number }) =>
  `${score.goals}.${score.behinds}${score.total === undefined ? '' : ` (${score.total})`}`

function ResourceStrip({ resources }: { resources: Resources }) {
 const entries = [
   ['Cash', resources.cash, '$m', 'Financial capital | Pays for staff, facilities, contracts and interventions. Unspent cash carries forward.'],
   ['Research', resources.research, 'pts', 'Research capacity | Spend on opposition, medical, draft and role analysis. Replenishes weekly; current storage cap is 12.'],
   ['Development', resources.development, 'pts', 'Development capacity | Allocate to player growth, role training, rehabilitation and tactical cohesion.'],
   ['Institution', resources.institutional, '', 'Institutional capital | Board confidence, player trust, supporter patience and club culture. Scale: 0-100.'],
  ] as const
  return (
    <div className="resource-strip" aria-label="Club resources">
      {entries.map(([label, value, suffix, tooltip]) => (
        <button className="resource has-tooltip" type="button" key={label} data-tooltip={tooltip}>
          <span>{label}</span>
          <strong>{value}{suffix && <small> {suffix}</small>}</strong>
        </button>
      ))}
    </div>
  )
}

function SectionHeading({ eyebrow, title, action }: { eyebrow: string; title: string; action?: React.ReactNode }) {
  return (
    <div className="section-heading">
      <div><span className="eyebrow">{eyebrow}</span><h2>{title}</h2></div>
      {action}
    </div>
  )
}

function StatusHeader({ state }: { state: GameState }) {
  const seasonComplete = state.season?.finals?.status === 'complete'
  const finalsActive = state.season?.finals?.status === 'active'
  const finalsFixture = finalsActive ? state.season?.finals?.fixtures.find(fixture => fixture.id === state.week?.fixtureId) : undefined
  return (
    <header className="status-header">
      <div className="fixture">
        <span className="eyebrow">{state.preseasonComplete === false ? `Season ${state.season?.season} — Pre-season` : seasonComplete ? 'Season complete — Premier decided' : finalsActive ? `Finals Week ${state.round - 19}${finalsFixture ? ` — ${finalsFixture.slot}` : ''}` : state.week?.venue === 'bye' ? `Round ${state.round} — Bye` : `Round ${state.round} - ${state.matchResult ? 'Review complete' : state.week?.venue === 'away' ? 'Away' : 'Home'}`}</span>
        <strong>{state.club.name}</strong>
        <span>{state.preseasonComplete === false ? 'Annual planning before Round 1' : seasonComplete ? `${state.season!.teams[state.season!.finals!.premierTeamId!].name} premiers` : finalsActive && state.week?.venue === 'bye' ? 'No Kingsport final this week' : state.week?.venue === 'bye' ? 'No scheduled match' : `v ${state.opponent.name}`}</span>
      </div>
      <div className="record-block"><span>Record</span><strong>{record(state)}</strong></div>
      <ResourceStrip resources={state.club.resources} />
    </header>
  )
}

const navTargets: Record<string, { phase?: GamePhase; tooltip: string }> = {
  Week: { phase: 'headquarters', tooltip: 'Headquarters | Current club state, required actions, staff choices and the next weekly action.' },
  Squad: { phase: 'squad', tooltip: 'Squad | Browse every player, flip collector cards and inspect attributes.' },
  Team: { phase: 'team', tooltip: 'Team | Select the side visually on the oval and assign positions.' },
  Development: { phase: 'development', tooltip: 'Development | Allocate coaching capacity, focus areas and assistants.' },
  Tactics: { phase: 'tactics-review', tooltip: 'Tactics | Review ball movement, width, contest, defence and tempo.' },
  Research: { phase: 'research', tooltip: 'Research | Spend capacity to turn broad staff assumptions into actionable opposition knowledge.' },
  Recruitment: { phase: 'recruitment', tooltip: 'Recruitment | Scout 76 draft prospects, maintain a watchlist and set end-of-season trade intent.' },
  Staff: { phase: 'staff', tooltip: 'Staff | Set the single annual development plan for yourself and your assistant coaches.' },
  Club: { phase: 'club', tooltip: 'Club | Stakeholder confidence, seasonal objective, long-term project and standing policies.' },
  League: { phase: 'league', tooltip: 'League | Live ladder, current fixtures and recorded results.' },
  History: { phase: 'history', tooltip: 'History | Club records, player legacies and career retrospective.' },
}

function Rail({ state, onReset, onNavigate }: { state: GameState; onReset: () => void; onNavigate: (phase: GamePhase) => void }) {
  const active = phaseLabels[state.phase] ?? 'Week'
  const seasonComplete = state.season?.finals?.status === 'complete'
  const preseason = state.preseasonComplete === false
  return (
    <aside className="rail">
      <div className="brand-mark" aria-label="Built for September"><span>BFS</span><strong>Built for<br />September</strong></div>
      <nav aria-label="Primary navigation">
        {navigation.map((item) => {
         const target = navTargets[item]
         const disabled = !target.phase || (preseason && item !== 'Week' && item !== 'Staff' && item !== 'History') || (!preseason && seasonComplete && item !== 'Week' && item !== 'League' && item !== 'Recruitment' && item !== 'History')
         const destination = preseason && item === 'Week' ? 'preseason' : seasonComplete && item === 'Week' ? 'next-round' : target.phase
         return <button key={item} type="button" className={`${active === item ? 'active' : ''} has-tooltip`} disabled={disabled} onClick={() => destination && onNavigate(destination)} data-tooltip={target.tooltip}><span>{item === 'Recruitment' ? 'RC' : item.slice(0, 2).toUpperCase()}</span>{item}</button>
       })}
      </nav>
      <button type="button" className="reset-button" onClick={onReset}>Reset Trial Season</button>
    </aside>
  )
}

function Meter({ label, value }: { label: string; value: number }) {
  return <div className="meter"><span>{label}</span><div><i style={{ width: `${value}%` }} /></div><strong>{value}</strong></div>
}



const teamGroups: { key: keyof TeamSelection; label: string }[] = [
  { key: 'backs', label: 'Full backs' }, { key: 'halfBacks', label: 'Half backs' },
  { key: 'centre', label: 'Centre line' }, { key: 'halfForwards', label: 'Half forwards' },
  { key: 'forwards', label: 'Forwards' }, { key: 'followers', label: 'Followers' },
  { key: 'interchange', label: 'Interchange' }, { key: 'emergencies', label: 'Emergencies' },
]

function TeamReview({ state, onPhase }: { state: GameState; onPhase: (phase: GamePhase, playerId?: string) => void }) {
  const playersById = useMemo(() => new Map(state.club.players.map(player => [player.id, player])), [state.club.players])
  const debut = state.decisions.D2.currentChoice === 'B'
  const playerRow = (entry: TeamPlayer) => {
    const player = playersById.get(entry.playerId)!
    const changed = entry.playerId === 'ari-dane' || entry.playerId === 'heath-mercer'
    return <div className={`team-player ${changed ? 'changed' : ''}`} key={`${entry.position}-${entry.playerId}`}><span>{entry.position}</span><div><strong>{player.name}</strong><small>{entry.role}</small></div>{changed && <em>{entry.playerId === 'ari-dane' ? (debut ? 'Coach in' : 'Emergency') : 'Staff selection'}</em>}</div>
  }
  return (
    <div className="screen"><button className="back-link" type="button" onClick={() => onPhase('headquarters')}>Back to dashboard</button><div className="page-title"><div><span className="eyebrow">Selection board - 22 players</span><h1>Team review</h1><p>Position groups are ordered from defence to attack. Individual condition remains visible.</p></div><span className={`status-pill ${debut ? 'override' : ''}`}>{debut ? 'D2-B Coach-locked' : 'D2-A Staff team'}</span></div>
      <section className="panel change-summary"><strong>{debut ? 'Ari Dane IN - Heath Mercer OUT' : 'Heath Mercer retained - Ari Dane emergency'}</strong><p>{debut ? 'Interchange role unchanged: inside midfielder. Tactical reliability reduces; outside pace and development exposure rise.' : 'The experienced interchange rotation remains intact. Ari continues the development plan without senior exposure.'}</p></section>
      <div className="team-groups">{teamGroups.map(group => <section className={`position-group ${group.key === 'interchange' ? 'focus-group' : ''}`} key={group.key}><h2>{group.label}<span>{state.staffPlan.team[group.key].length}</span></h2>{state.staffPlan.team[group.key].map(playerRow)}</section>)}</div>
      <div className="sticky-actions"><button className="text-button" type="button" onClick={() => onPhase('player-detail', 'ari-dane')}>Change Ari decision</button><button className="primary-action" type="button" onClick={() => onPhase('tactics-review')}>Continue to tactics</button></div>
    </div>
  )
}

function TacticsReview({ state, setState }: { state: GameState; setState: React.Dispatch<React.SetStateAction<GameState>> }) {
  const updateTactics = (id: GameState['tactics'][number]['id'], choice: ChoiceLetter) => setState(current => {
    const tactics = current.tactics.map(tactic => tactic.id === id ? { ...tactic, currentChoice: choice } : tactic)
    return {
      ...current,
      tactics,
      decisions: { ...current.decisions, [id]: { ...current.decisions[id], currentChoice: choice } },
      staffPlan: { ...current.staffPlan, matchPlan: tactics },
    }
  })
  return <TacticalOverrides
    round={state.round}
    opponentName={state.opponent.name}
    opponentStyle={state.opponent.style}
    opponentStrength={state.opponent.strengths[0]}
    opponentWeakness={state.opponent.weaknesses[0]}
    tactics={state.tactics}
    conditionalInstruction={state.conditionalInstruction}
    researchInsights={state.researchPlan.topics.filter(topic => topic.isSelected).map(topic => topic.tacticalAdvice)}
    onTacticChange={updateTactics}
    onConditionalChange={instruction => setState(current => ({
      ...current,
      conditionalInstruction: instruction,
      decisions: { ...current.decisions, D7: { ...current.decisions.D7, currentChoice: instruction.id.slice(-1) as ChoiceLetter } },
      staffPlan: { ...current.staffPlan, conditionalInstruction: instruction },
    }))}
    onRestore={staffConditional => setState(current => {
      const tactics = current.tactics.map(tactic => ({ ...tactic, currentChoice: tactic.staffChoice }))
      const decisions = { ...current.decisions }
      tactics.forEach(tactic => { decisions[tactic.id] = { ...decisions[tactic.id], currentChoice: tactic.staffChoice } })
      decisions.D7 = { ...decisions.D7, currentChoice: 'A' }
      return { ...current, tactics, conditionalInstruction: staffConditional, decisions, staffPlan: { ...current.staffPlan, matchPlan: tactics, conditionalInstruction: staffConditional } }
    })}
    onBack={() => setState(current => setPhase(current, 'headquarters'))}
    onConfirm={() => setState(current => setPhase(current, 'headquarters'))}
  />
}

function ResearchView({ state, setState }: { state: GameState; setState: React.Dispatch<React.SetStateAction<GameState>> }) {
  return <ResearchScreen
    plan={state.researchPlan}
    archive={state.researchArchive}
    available={state.club.resources.research}
    maximum={state.club.maxResearchCapacity}
    weeklyGeneration={state.club.weeklyResearchGeneration}
    opponentStyle={state.opponent.style}
    opponentAssessment={state.opponent.assessment}
    onToggle={topicId => setState(current => toggleResearchTopic(current, topicId))}
    onRestore={() => setState(current => restoreStaffResearch(current))}
    onBack={() => setState(current => setPhase(current, 'headquarters'))}
    onContinue={() => setState(current => setPhase(current, 'headquarters'))}
  />
}

function Confirmation({ state, onPhase, setState }: { state: GameState; onPhase: (phase: GamePhase) => void; setState: React.Dispatch<React.SetStateAction<GameState>> }) {
  const debut = state.decisions.D2.currentChoice === 'B'
  const tacticOverrides = state.tactics.filter(tactic => tactic.currentChoice !== tactic.staffChoice).length + (state.conditionalInstruction?.id === 'D7-A' ? 0 : 1)
  const matchPlan = state.tactics.map(tactic => tactic.options.find(option => option.letter === tactic.currentChoice)?.label).join(' / ')
  const researchCount = state.researchPlan.topics.filter(topic => topic.isSelected).length
  const ready = isReadyForMatch(state)
  const requiredActions = ready ? [] : getWeeklyActions(state).filter(a => a.status === 'required')

  const handleResolve = () => {
    if (!ready) return
    // Step 3-5: Commit deterministic match context and lock decisions
    const committedState = commitAndLockForMatch(state)
    // Step 4: Overwrite bfs-matchday-autosave with committed context + locked decisions
    saveMatchdayState(committedState)
    // Step 6-7: Simulate match and persist resulting working state (via useEffect)
    const resolvedState = resolveCurrentRound(committedState)
    setState(resolvedState)
  }

  return (
    <div className="screen confirmation-screen">
      <button className="back-link" type="button" onClick={() => onPhase('tactics-review')}>Back to tactics</button>
      <section className="confirmation-card">
        <span className="eyebrow">{ready ? `Round ${state.round} - Plan ready` : 'Required actions incomplete'}</span>
        <h1>Start Match</h1>
        <p>Your committed plan will be autosaved, overwriting the previous matchday autosave. The match cannot be undone or rerolled.</p>
        {!ready && (
          <div className="validation-block">
            <strong>Cannot start match — resolve these first:</strong>
            <ul>
              {requiredActions.map(action => (
                <li key={action.phase}>{action.label}: {action.statusLabel}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="confirm-list">
          <div><span>Selection</span><strong>{debut ? `D2-B - Ari Dane selected for Round ${state.round}` : 'D2-A - Staff team retained'}</strong></div>
          <div><span>Research</span><strong>{researchCount ? `${researchCount} investigation${researchCount === 1 ? '' : 's'} · ${state.staffPlan.research.points} spent · ${state.club.resources.research} remaining` : 'Staff report only · no spend'}</strong></div>
          <div><span>Match plan</span><strong>{matchPlan}</strong></div>
          <div><span>Conditional</span><strong>{state.conditionalInstruction?.id} - {state.conditionalInstruction?.label}</strong></div>
          <div><span>Tactical overrides</span><strong>{tacticOverrides === 0 ? 'None - staff plan retained' : `${tacticOverrides} Coach override${tacticOverrides === 1 ? '' : 's'}`}</strong></div>
        </div>
        <small>Seed {state.seed} — deterministic prototype result</small>
        <br />
        <span className="save-note">✓ This is the only matchday autosave. Overwrites when you start the match.</span>
        <hr className="divider save-divider" />
        <button className="primary-action large" type="button" onClick={handleResolve} disabled={!ready}>Start Match • Autosave & Commit</button>
      </section>
    </div>
  )
}

export function Consequences({ state, onAdvance }: { state: GameState; onAdvance: () => void }) {
  const result = state.matchResult!
  const report = state.consequenceReport!
  const won = result.ourScore.total > result.opponentScore.total
  return <div className="screen review-screen"><div className="result-hero"><span className="eyebrow">Full time - Round {result.round}</span><strong className="result-label">{won ? 'Kingsport wins' : 'Final result'}</strong><div className="scoreboard"><div><span>Kingsport Imperials</span><strong>{scoreText(result.ourScore)}</strong></div><b>{result.ourScore.total - result.opponentScore.total > 0 ? `+${result.ourScore.total - result.opponentScore.total}` : result.ourScore.total - result.opponentScore.total}</b><div><span>{result.opponent}</span><strong>{scoreText(result.opponentScore)}</strong></div></div><p>{clean(result.tacticalAnalysis.summary)}</p></div>
    <section className="panel quarter-table"><SectionHeading eyebrow="Score progression" title="Quarter by quarter" /><div className="quarters">{result.quarters.map(quarter => <div key={quarter.quarter}><span>Q{quarter.quarter}</span><strong>{scoreText(quarter.ourScore)}</strong><strong>{scoreText(quarter.opponentScore)}</strong><p>{clean(quarter.description)}</p></div>)}</div></section>
    <section className="panel injury-report"><SectionHeading eyebrow="Medical report" title="Match injuries" />{report.injuries.length === 0 ? <p className="no-injuries">No reportable Kingsport injuries.</p> : report.injuries.map(injury => <div className="consequence injury-consequence" key={injury.id}><strong>{injury.player} — {injury.severity} {injury.type}</strong><span>{injury.initialWeeks} {injury.initialWeeks === 1 ? 'week' : 'weeks'} estimated{injury.isRecurrence ? ` · recurrence ${injury.recurrenceCount}` : ''}</span><p>{clean(injury.description)}</p></div>)}</section>
    <div className="review-grid"><section className="panel"><SectionHeading eyebrow="Why the match moved" title="Causes" />{result.momentumShifts.map(item => <div className={`event ${item.impact}`} key={`${item.quarter}-${item.time}`}><span>Q{item.quarter} {item.time}</span><p>{clean(item.description)}</p></div>)}<div className="analysis-bars">{[['Width', result.tacticalAnalysis.widthEffectiveness], ['Contest', result.tacticalAnalysis.contestEffectiveness], ['Defence', result.tacticalAnalysis.defensiveEffectiveness], ['Tempo', result.tacticalAnalysis.tempoEffectiveness]].map(([label, value]) => <Meter key={label} label={String(label)} value={Number(value)} />)}</div></section>
      <section className="panel"><SectionHeading eyebrow="Club state" title="Consequences" /><div className="ledger"><div><span>Research</span><strong>{signed(report.resourceChanges.research ?? 0)}</strong></div><div><span>Institution</span><strong>{signed(report.resourceChanges.institutional ?? 0)}</strong></div><div><span>Cash</span><strong>{signed(report.resourceChanges.cash ?? 0)}</strong></div></div>{report.developmentProgress.map(item => <div className="consequence" key={item.player}><strong>{item.player} — weekly development +{item.progress}</strong><span>{item.pointsAllocated} pts · {item.focus} · {item.matchExposure ? 'senior exposure' : 'training only'}</span><p>{clean(item.note)}</p></div>)}{report.developmentPrograms.map(item => <div className="consequence development-program" key={item.program}><strong>{item.program} — {item.pointsAllocated} pts</strong><span>{item.effect}</span><p>{clean(item.note)}</p></div>)}{report.moraleChanges.map(item => <div className="consequence" key={item.player}><strong>{item.player} morale {signed(item.change)}</strong><p>{clean(item.reason)}</p></div>)}</section>
      <section className="panel performances"><SectionHeading eyebrow="Selection evidence" title="Key performances" />{result.playerPerformances.map(player => <div key={player.player}><strong>{player.rating.toFixed(1)}</strong><div><h3>{player.player}</h3><span>{player.disposals} disp - {player.goals} goals - {player.tackles} tackles</span><p>{clean(player.notes)}</p></div></div>)}</section>
      <section className="panel"><SectionHeading eyebrow="New knowledge" title="What carries forward" />{report.researchKnowledge.map(item => <div className="consequence" key={item.subject}><strong>{item.subject} - confidence {item.newConfidence}</strong><p>{clean(item.summary)}</p></div>)}{report.leagueStories.map(item => <div className="consequence" key={item.headline}><strong>{item.headline}</strong><p>{clean(item.summary)}</p></div>)}</section></div>
    <div className="sticky-actions"><span>Apply consequences and advance to the next week.</span><button className="primary-action" type="button" onClick={onAdvance}>{state.round === 19 ? 'Complete Home-and-Away Season' : `Complete Round ${state.round}`}</button></div>
  </div>
}

function NextRound({ state, onContinue, onOpenRecruitment }: { state: GameState; onContinue: () => void; onOpenRecruitment: () => void }) {
  const finalRound = state.season ? Math.max(...state.season.fixtures.map(fixture => fixture.round)) : null
  const seasonComplete = state.season?.finals?.status === 'complete'
  const finalsActive = state.season?.finals?.status === 'active'
  const kingsportFinalsAlive = Boolean(finalsActive && state.season?.finals && isTeamAliveInFinals(state.season.finals, state.season.userTeamId))
  const completedRound = state.season?.completedThroughRound ?? Math.max(0, state.round - 1)
  const change = state.consequenceReport?.nextWeekStaffPlanChanges[0]
  const ari = state.club.players.find(player => player.id === 'ari-dane')!
  const premier = seasonComplete && state.season?.finals?.premierTeamId ? state.season.teams[state.season.finals.premierTeamId] : null
  return <div className="screen next-round">
    <div className="round-four-hero"><span className="eyebrow">Season progression</span><h1>{seasonComplete ? 'Season schedule complete' : finalsActive && !kingsportFinalsAlive ? 'Final Eight ready' : finalsActive ? `Finals Week ${Math.min(4, state.round - 19)} handover` : `Round ${state.round} handover`}</h1><p>{seasonComplete ? 'The Grand Final is complete. Review every finals result before entering list management.' : finalsActive && !kingsportFinalsAlive ? 'Kingsport finished outside the Final Eight or has been eliminated. Simulate the remaining finals once, then review the complete series before opening the Draft Centre.' : finalsActive ? 'The previous finals stage is permanently applied. Continue to Kingsport’s next Final Eight stage.' : `Round ${completedRound} is permanently applied. Staff are ready to build a fresh plan for the next scheduled opponent.`}</p><div className="new-record"><span>Updated record</span><strong>{record(state)}</strong></div></div>
    <div className="round-four-grid"><section className="panel"><SectionHeading eyebrow="Applied ledger" title="Updated resources" /><ResourceStrip resources={state.club.resources} /><p className="ledger-note">Research capacity is {state.club.researchCapacity} of {state.club.maxResearchCapacity}. Development, player condition and league results are saved.</p></section><section className="panel story-card"><SectionHeading eyebrow="Next staff action" title={seasonComplete ? 'Review the Final Eight' : finalsActive ? 'Finals progression' : 'Opponent preparation'} /><p>{seasonComplete ? 'Every finals result is shown below. The Draft Centre remains a separate action.' : finalsActive && !kingsportFinalsAlive ? 'One simulation action resolves the remaining Final Eight. No repeated empty finals weeks are required.' : finalsActive ? 'Open Kingsport’s next finals week and prepare for the scheduled opponent.' : change?.description ?? 'Staff will prepare the next scheduled fixture.'}</p><div className="story-stats"><span>Tactical discipline <strong>{ari.abilities.tacticalDiscipline}</strong></span><span>Banked progress <strong>{ari.development.progress}</strong></span><span>Senior matches <strong>{ari.development.seniorMatches}</strong></span></div><span className="status-pill override">{seasonComplete ? 'Season complete' : finalsActive ? `Finals active · ${finalRound} regular rounds complete` : `Round ${completedRound} complete`}</span></section></div>
    {seasonComplete && state.season?.finals && <section className="panel finals-panel" aria-labelledby="finals-series-results"><SectionHeading eyebrow="Final Eight complete" title={premier ? `${premier.name} premiers` : 'Finals Series Results'} /><h3 id="finals-series-results">Finals Series Results</h3><div className="finals-bracket">{([1, 2, 3, 4] as const).map(week => <section className="finals-week" key={week}><h3>Week {week}<small>{week === 1 ? 'QFs / EFs' : week === 2 ? 'SFs' : week === 3 ? 'PFs' : 'Grand Final'}</small></h3>{state.season!.finals!.fixtures.filter(fixture => fixture.week === week).map(fixture => { const result = fixture.result; return <article className={`final-card ${fixture.stage === 'grand-final' ? 'grand-final' : ''}`} aria-label={`${fixture.slot}: ${state.season!.teams[fixture.homeTeamId].name} versus ${state.season!.teams[fixture.awayTeamId].name}`} key={fixture.id}><span>{fixture.slot} · {fixture.venue}</span><strong>{state.season!.teams[fixture.homeTeamId].name} {result ? scoreText(result.homeScore) : '—'}</strong><strong>{state.season!.teams[fixture.awayTeamId].name} {result ? scoreText(result.awayScore) : '—'}</strong></article>})}</section>)}</div></section>}
    <div className="sticky-actions"><span>{seasonComplete ? 'Finals results are complete — continue when ready for list management' : finalsActive && !kingsportFinalsAlive ? 'One action will simulate the remaining Final Eight and preserve every result' : finalsActive ? 'Ready for Kingsport’s next finals stage' : 'Ready for the next scheduled week'}</span>{seasonComplete ? <button className="primary-action" type="button" onClick={onOpenRecruitment}>Continue to Draft Centre</button> : <button className="primary-action" type="button" onClick={onContinue}>{finalsActive && !kingsportFinalsAlive ? 'Simulate Remaining Finals' : finalsActive ? `Continue to Finals Week ${state.round - 19}` : `Continue to Round ${state.round}`}</button>}</div>
  </div>
}

function PreseasonHub({ state, onCoachPlan, onBeginSeason }: { state: GameState; onCoachPlan: () => void; onBeginSeason: () => void }) {
  const drafted = state.recruitment.draft.order.filter(pick => pick.clubId === state.season?.userTeamId && pick.prospectId).length
  const delisted = state.recruitment.delistedPlayerIds.length
  const listReady = state.club.players.length === 24
  const coachReady = state.coachDevelopment.isLocked && state.preseasonCoachPlanConfirmed === true
  const ready = listReady && coachReady
  return <div className="screen preseason-screen">
    <div className="round-four-hero"><span className="eyebrow">Season {state.season?.season} · Pre-season</span><h1>Prepare the club before Round 1</h1><p>The off-season list build is complete. Confirm the annual football-department plan before normal weekly match decisions begin.</p><div className="new-record"><span>Competition record</span><strong>0-0-0</strong></div></div>
    <div className="round-four-grid">
      <section className="panel story-card"><SectionHeading eyebrow="List management complete" title="24-player squad ready" /><p>{drafted} draftees joined the club and {delisted} players were delisted. The playing list is locked at {state.club.players.length} of 24.</p><span className={`status-pill ${listReady ? '' : 'required'}`}>{listReady ? 'Complete' : 'Action required'}</span></section>
      <section className="panel story-card"><SectionHeading eyebrow="Annual decision" title="Coach Development Plan" /><p>{coachReady ? `The Season ${state.coachDevelopment.season} plan is locked. Staff can now build the Round 1 programme.` : `Review and lock the Season ${state.coachDevelopment.season} allocation before entering the weekly season loop.`}</p><button className={coachReady ? 'text-button' : 'primary-action'} type="button" onClick={onCoachPlan}>{coachReady ? 'Review Locked Coach Plan' : 'Set Annual Coach Plan'}</button><span className={`status-pill ${coachReady ? '' : 'override'}`}>{coachReady ? 'Complete' : 'Required before Round 1'}</span></section>
    </div>
    <div className="sticky-actions"><span>{ready ? 'Pre-season complete — Round 1 is ready' : 'Complete the annual coach plan before entering Round 1'}</span><button className="primary-action" type="button" disabled={!ready} onClick={onBeginSeason}>Begin Season {state.season?.season} — Open Round 1</button></div>
  </div>
}

function SquadView({ state, onPhase }: { state: GameState; onPhase: (phase: GamePhase, playerId?: string) => void }) {
  return (
    <SquadScreen
      players={state.club.players}
      development={state.staffPlan.development}
      onBack={() => onPhase('headquarters')}
      onPlayerClick={player => onPhase('player-detail', player.id)}
    />
  )
}

function DevelopmentView({ state, setState }: { state: GameState; setState: React.Dispatch<React.SetStateAction<GameState>> }) {
  return (
    <DevelopmentPlan
      players={state.club.players}
      allocations={state.staffPlan.development}
      staffBaseline={state.week?.staffDefaults?.development ?? STAFF_DEVELOPMENT_BASELINE}
      capacity={state.club.developmentCapacity}
      coachingStaff={state.club.staff}
      onComplete={() => setState(current => setPhase(current, 'headquarters'))}
      onUpdate={newAllocations => {
        setState(current => ({
          ...current,
          staffPlan: { ...current.staffPlan, development: newAllocations }
        }))
      }}
    />
  )
}

function TeamSelectionView({ state, setState }: { state: GameState; setState: React.Dispatch<React.SetStateAction<GameState>> }) {
  return (
    <div className="screen">
      <button type="button" className="back-link" onClick={() => setState(current => setPhase(current, 'headquarters'))}>Back to dashboard</button>
      <div className="page-title">
        <div>
          <span className="eyebrow">Selection board - 22+6 players</span>
          <h1>Team Selection</h1>
          <p>Drag players to positions. Click empty spots to cycle. Hover for details.</p>
        </div>
        <span className="status-pill">Drag & drop active</span>
      </div>
      <TeamSelectionOval
        players={state.club.players}
        team={state.staffPlan.team}
        onChange={newTeam => setState(current => ({
          ...current,
          decisions: { ...current.decisions, D2: { ...current.decisions.D2, currentChoice: 'D', isLocked: true } },
          staffPlan: { ...current.staffPlan, team: newTeam },
        }))}
      />
      <div className="sticky-actions">
        <span>Changes auto-saved</span>
        <button className="primary-action" type="button" onClick={() => setState(current => setPhase(current, 'headquarters'))}>Save Team — Return to Week</button>
      </div>
    </div>
  )
}

function StaffView({ state, setState }: { state: GameState; setState: React.Dispatch<React.SetStateAction<GameState>> }) {
  return <StaffDevelopment
    seniorCoach={state.club.seniorCoach}
    assistants={state.club.staff}
    plan={state.coachDevelopment}
    onUpdate={coachDevelopment => setState(current => ({ ...current, coachDevelopment }))}
    onComplete={() => setState(current => current.preseasonComplete === false
      ? { ...setPhase(current, 'preseason'), preseasonCoachPlanConfirmed: true }
      : setPhase(current, 'headquarters'))}
    state={state}
  />
}

function RecruitmentView({ state, setState }: { state: GameState; setState: React.Dispatch<React.SetStateAction<GameState>> }) {
  const seasonComplete = state.season?.finals?.status === 'complete'
  return <RecruitmentScreen
    state={state}
    onWatchlist={prospectId => setState(current => ({ ...current, recruitment: toggleProspectWatchlist(current.recruitment, prospectId) }))}
    onScout={prospectId => setState(current => ({ ...current, recruitment: scoutProspect(current.recruitment, prospectId, current.round) }))}
    onTradeStatus={(playerId: string, status: TradeStatus) => setState(current => ({ ...current, recruitment: setPlayerTradeStatus(current.recruitment, playerId, status) }))}
    onTradeResponse={(offerId, accept) => setState(current => respondToTradeOffer(current, offerId, accept))}
    onBeginDraft={() => setState(current => current.season ? ({ ...current, recruitment: beginDraft(current.recruitment, current.season) }) : current)}
    onDraft={prospectId => setState(current => selectDraftProspect(current, prospectId))}
    onToggleDelist={playerId => setState(current => ({ ...current, recruitment: togglePlayerDelisting(current.recruitment, playerId) }))}
    onStartPreseason={() => setState(current => startNextSeasonPreseason(current))}
    onBack={() => setState(current => setPhase(current, seasonComplete ? 'next-round' : 'headquarters'))}
  />
}

export default function App() {
  const [state, setState] = useState<GameState>(loadState)
  useEffect(() => { saveWorkingState(state) }, [state])
  const go = (phase: GamePhase, playerId?: string) => setState(current => {
    const next = setPhase(current, phase)
    if (phase === 'player-detail' && playerId) {
      return { ...next, selectedPlayerId: playerId }
    }
    if (phase === 'headquarters') return setPhase(current, weekPhaseFor(current))
    if (phase !== 'recruitment' || !next.season) return next
    return next.season.finals?.status === 'complete' ? openTradePeriod(next) : next
  })
  const chooseAri = (choice: 'staff' | 'debut') => setState(current => selectAriPlan(current, choice))
  const resolve = () => setState(current => resolveCurrentRound(current))
  const advance = () => setState(current => advanceAfterRound(current))
  const continueSeason = () => setState(current => continueToNextRound(current))
  const reset = () => { clearWorkingState(); clearMatchdayState(); setState(createInitialState(createCareerSeed())) }
  return <div className="app-shell"><Rail state={state} onReset={reset} onNavigate={go} /><div className="workspace"><StatusHeader state={state} /><main>
    {state.phase === 'preseason' && <PreseasonHub state={state} onCoachPlan={() => go('staff')} onBeginSeason={() => setState(current => {
      if (!current.coachDevelopment.isLocked || current.preseasonCoachPlanConfirmed !== true || current.club.players.length !== 24 || !current.season) return current
      const seasonNumber = current.season.season
      return { ...current, preseasonComplete: true, phase: 'headquarters', recruitment: createRecruitmentState(current.seed + (seasonNumber * 1000), seasonNumber, current.club.players) }
    })} />}
    {state.phase === 'headquarters' && <Dashboard state={state} onAction={go} onAdvance={resolve} />}
    {state.phase === 'left-field' && <LeftFieldScreen state={state} onChoose={choiceId => setState(current => resolveLeftFieldChoice(current, choiceId))} onDelegate={() => setState(current => delegateLeftFieldEvent(current))} onReturn={() => go('headquarters')} />}
    {state.phase === 'player-detail' && state.selectedPlayerId && (() => {
      const player = state.club.players.find(p => p.id === state.selectedPlayerId)
      return player ? (
        <PlayerView
          player={player}
          state={state}
          onPhase={go}
          onAriChoice={player.id === 'ari-dane' ? chooseAri : undefined}
        />
      ) : (
        <div className="screen"><p>Player not found</p><button className="primary-action" onClick={() => go('squad')}>Back to Squad</button></div>
      )
    })()}
    {state.phase === 'squad' && <SquadView state={state} onPhase={go} />}
    {state.phase === 'development' && <DevelopmentView state={state} setState={setState} />}
    {state.phase === 'team' && <TeamSelectionView state={state} setState={setState} />}
    {state.phase === 'staff' && <StaffView state={state} setState={setState} />}
    {state.phase === 'team-review' && <TeamReview state={state} onPhase={go} />}
    {state.phase === 'tactics-review' && <TacticsReview state={state} setState={setState} />}
    {state.phase === 'research' && <ResearchView state={state} setState={setState} />}
    {state.phase === 'recruitment' && <RecruitmentView state={state} setState={setState} />}
    {state.phase === 'league' && <LeagueScreen state={state} onBack={() => go('headquarters')} />}
    {state.phase === 'history' && <HistoryScreen state={state} onBack={() => go('headquarters')} />}
    {state.phase === 'club' && <ClubScreen state={state} onBack={() => go('headquarters')} onAction={action => setState(current => action(current))} />}
    {state.phase === 'confirmation' && <Confirmation state={state} onPhase={go} setState={setState} />}
    {state.phase === 'match-day' && (!state.matchResult || !state.consequenceReport) && <Confirmation state={state} onPhase={go} setState={setState} />}
    {(state.phase === 'consequences' || (state.phase === 'match-day' && state.matchResult && state.consequenceReport)) && <Consequences state={state} onAdvance={advance} />}
    {state.phase === 'next-round' && <NextRound state={state} onContinue={continueSeason} onOpenRecruitment={() => go('recruitment')} />}
  </main></div></div>
}